import React, { useContext, useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { createFieldsFromFormFields } from '@components/forms/utils/create-fields-from-form-fields';
import { FormField } from '@components/modals/add-holder-modal/src/add-holder-modal.type';
import { BaseProcessStep, STEP_SUCCESS } from '@constants/process-steps';
import { ModalContext } from '@contexts/modal/modal-context';
import { BOOKING_FORM_FIELDS, TICKET_HOLDER_FORM_FIELDS } from '@domain/admission';
import { useGetOfferAddon } from '@hooks/addons/use-get-offer-addons';
import { useImportMultiInvite } from '@hooks/import/use-import-multi-invite';
import { useAddGuest } from '@hooks/use-add-guest/use-add-guest';
import { useBulkProcessNavigation } from '@hooks/use-bulk-process-navigation';
import { useProcessForm } from '@hooks/use-process-form/use-process-form';
import { UseProcessFormReturn } from '@hooks/use-process-form/use-process-form.types';
import { IMPORT_TYPE_OFFLINE_SALE } from '@pages/events/utils/import-process-utils';
import { handleBulkProcessSubmission } from '@utils/bulk-process/bulk-process-utils';

import { OfflineSalesSteps } from './offline-sales-steps';
import {
  MULTIPLE_OFFLINE_SELL,
  OfflineSalesForm,
  ProcessOfflineSalesStep,
  SINGLE_OFFLINE_SELL,
} from './process-offline-sales.type';

/*
 * ProcessOfflineSales Component
 *
 * This component manages the process of recording offline sales for an event, supporting both single and bulk (multi-sale) flows.
 *
 * Features:
 * - Allows selection of sale type (single or multiple), event, offer, and channel.
 * - Collects customer and sale details via a form.
 * - Supports bulk offline sales by parsing multi-line text input for multiple sales.
 * - Integrates with shared bulk process utilities for parsing, submission, and navigation.
 * - Displays success and summary modals upon completion.
 *
 * This component is part of the unified bulk process system, sharing navigation and utility logic with other processes (box office, invite guest, import CSV).
 */

export const ProcessOfflineSales = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const { handleCloseModal } = useContext(ModalContext);

  // Use the extended process form hook with offline sales specific fields and proper typing
  const processForm: UseProcessFormReturn = useProcessForm({
    includeAddOns: true,
    includeSaleType: true,
    includeMultiText: true,
    includeBookingFormFields: true,
    includeTicketHolderFormFields: true,
    additionalDefaults: {
      saleType: SINGLE_OFFLINE_SELL,
      bookingFormFields: {},
      ticketHolderFormFields: [],
    },
  });

  const {
    methods,
    eventId,
    quantity,
    addOns,
    saleType: saleTypeRaw,
    bookingFormFields,
    offer,
    event,
    step: processStep,
    setStep: setProcessStep,
  } = processForm;

  // Type-safe wrappers to match offline sales step types
  const step = processStep as ProcessOfflineSalesStep;
  const setStep = (newStep: ProcessOfflineSalesStep) => {
    setProcessStep(newStep as BaseProcessStep);
  };

  // Type the saleType properly
  const saleType = saleTypeRaw as typeof SINGLE_OFFLINE_SELL | typeof MULTIPLE_OFFLINE_SELL;

  // Get email from booking form fields
  const email = bookingFormFields?.email as string | undefined;

  const { data: addons } = useGetOfferAddon({
    offerId: offer?.id,
    enabled: offer && offer?.addOns?.length > 0,
  });
  const availableAddons = addons?.filter((addon) => {
    if (!addon.quantity) return true;

    return addon.soldItems < addon.quantity;
  });

  const {
    data: addGuestData,
    mutate: addGuest,
    isSuccess: addGuestSuccess,
    isPending: isAddingGuest,
  } = useAddGuest({ hideToast: true });
  const {
    data: importMultiInviteData,
    mutate: importMultiInvite,
    isSuccess: importSuccess,
    isPending: isImporting,
  } = useImportMultiInvite({ hideToast: true });

  // Use the shared import navigation hook
  const { closeModal } = useBulkProcessNavigation({
    eventId,
    importType: IMPORT_TYPE_OFFLINE_SALE,
    importSuccess,
    importData: importMultiInviteData,
  });

  const handleSingleOfflineSubmit = (e: OfflineSalesForm) => {
    if (typeof e.addOns === 'string') e.addOns = [e.addOns];
    addGuest({
      formValue: {
        offerId: e.offerId,
        bookingFormFields: createFieldsFromFormFields<OfflineSalesForm>(e, BOOKING_FORM_FIELDS) as FormField[],
        ticketHolderFormFields: createFieldsFromFormFields<OfflineSalesForm>(
          e,
          TICKET_HOLDER_FORM_FIELDS
        ) as FormField[][],
        numTickets: e.quantity,
        channelId: e.channelId,
        addOns: e.addOns ? e.addOns.map((addon) => ({ id: addon, quantity: e.quantity })) : [],
      },
    });
  };

  const handleMultiOfflineSaleSubmit = (e: OfflineSalesForm) => {
    if (typeof e.addOns === 'string') e.addOns = [e.addOns];
    if (e.multiOfferText) {
      handleBulkProcessSubmission(
        e.multiOfferText,
        {
          offerId: e.offerId,
          channelId: e.channelId,
          eventId: e.eventId,
          addOns: e.addOns,
        },
        importMultiInvite
      );
    }
  };

  const onSubmit = (e: OfflineSalesForm) => {
    if (saleType === SINGLE_OFFLINE_SELL) {
      handleSingleOfflineSubmit(e);
    } else if (saleType === MULTIPLE_OFFLINE_SELL) {
      handleMultiOfflineSaleSubmit(e);
    }
  };

  useEffect(() => {
    if (addGuestSuccess) {
      setStep(STEP_SUCCESS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addGuestSuccess]);

  return (
    <form className='flex justify-end flex-col relative' ref={formRef}>
      <FormProvider {...methods}>
        <OfflineSalesSteps
          step={step}
          setStep={setStep}
          offer={offer}
          event={event}
          saleType={saleType}
          quantity={quantity}
          addGuestData={addGuestData}
          availableAddons={availableAddons ?? []}
          selectedAddonIds={typeof addOns === 'string' ? [addOns] : addOns}
          email={email}
          isSubmitting={isAddingGuest || isImporting}
          closeModal={closeModal}
          handleCloseModal={handleCloseModal}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </form>
  );
};
